import { createSelector } from 'reselect';
import last from 'lodash/last';
import isEmpty from 'lodash/isEmpty';
import type { GetChecklistRequestV2 } from '@zola/svc-web-api-ts-client';
import type { RootState } from '../../reducers';
import type {
  ChecklistTasksByIdType,
  ChecklistTaskGroupType,
  WChecklistTaskViewWithGroupType,
} from '../../reducers/checklist/tasks';
import {
  getUserId,
  getPartnerUserId,
  getPartnerFirstName,
  getOwnerAccountUserId,
  getOwnerFirstName,
} from '../user/userSelectors';
import { ChecklistReducerStateType } from '../../reducers/checklist';
import { ChecklistChecklistReducerStateType, OptionType } from '../../reducers/checklist/checklist';

const DEFAULT_OBJECT: ChecklistTasksByIdType = {};

export const getChecklist = (state: RootState): ChecklistReducerStateType =>
  state.checklist || DEFAULT_OBJECT;

export const getChecklistUIState = (state: RootState): ChecklistChecklistReducerStateType =>
  state.checklist.checklist || DEFAULT_OBJECT;

export const getChecklistLoading = (state: RootState): boolean => {
  const checklist = getChecklistUIState(state);
  return checklist.busy;
};

export const getChecklistTaskGroups = (state: RootState): ChecklistTaskGroupType[] => {
  const checklist = getChecklist(state);
  return checklist.tasks.filteredTasksGroups;
};

export const getChecklistTasks = (state: RootState): ChecklistTasksByIdType =>
  state.checklist.tasks.byId || {};

export const getChecklistTaskById = (
  state: RootState,
  taskId: string
): WChecklistTaskViewWithGroupType => {
  const tasks = getChecklistTasks(state);

  return tasks[taskId] || DEFAULT_OBJECT;
};

export const getSoonestChecklistTask = (
  state: RootState
): WChecklistTaskViewWithGroupType | undefined => {
  const groups = getChecklistTaskGroups(state);
  const tasks = getChecklistTasks(state);
  const firstId = groups[0].taskIds[0];

  return tasks[firstId];
};

export const getActiveTaskId = (state: RootState): string | null =>
  state.checklist.tasks.selectedId;

export const getActiveOrInvitedPartner = (
  state: RootState
): { userObjectId: string; partnerUserObjectId: string; status: string } => {
  const partnerArray = state.user.partnerContext;
  return (
    partnerArray.filter(p => p.status !== 'REVOKED_INVITE' && p.status !== 'REMOVED_USER')[0] || {}
  );
};

export const getTaskFilters = (state: RootState): GetChecklistRequestV2.FiltersEnum[] => {
  const checklist = getChecklistUIState(state);
  return checklist.filters;
};

export const getTaskFacets = (state: RootState): { options: OptionType[] }[] => {
  const checklist = getChecklistUIState(state);
  return checklist.facets;
};

export const getFacetValue = (state: RootState, props: { facetKey: string }): string =>
  props.facetKey;

export const makeGetTaskFacetLabel = (): ((
  state: RootState,
  props: { facetKey: string }
) => string | undefined) =>
  createSelector(getTaskFacets, getFacetValue, (facets, facetValue) => {
    const allOptions: OptionType[] = [];

    facets.forEach(facet => {
      facet.options.forEach(option => allOptions.push(option));
    });

    const optionObj: OptionType = allOptions.find(option => option.value === facetValue) || {};

    return optionObj.label;
  });
export const getNoFilterResults = (state: RootState): boolean => {
  const checklist = getChecklistUIState(state);

  return checklist.noFilterResults;
};

export const getIsLastTask = (state: RootState, taskId: string): boolean => {
  const taskGroups = getChecklistTaskGroups(state);
  const lastGroup = last(taskGroups);
  if (!lastGroup) {
    return false;
  }
  const lastTaskIdInGroup = last(lastGroup.taskIds);

  // not sure why taskId is a string...
  return parseInt(taskId, 10) === lastTaskIdInGroup;
};

export const getIsCustomTask = (state: RootState, taskId: string): boolean => {
  const task = getChecklistTaskById(state, taskId);

  return !isEmpty(task) && !task.checklist_template_task_id;
};

export const getIsTaskAssignedToPartnerAccount = (state: RootState, taskId: string): boolean => {
  const task = getChecklistTaskById(state, taskId);
  const partnerId = getPartnerUserId(state);

  return task.assigned_user_object_id === partnerId;
};

export const getIsTaskAssignedToOwnerAccount = (state: RootState, taskId: string): boolean => {
  const task = getChecklistTaskById(state, taskId);
  const userId = getOwnerAccountUserId(state);

  return task.assigned_user_object_id === userId;
};

export const getIsTaskAssignedToCurrentUser = (state: RootState, taskId: string): boolean => {
  const task = getChecklistTaskById(state, taskId);
  const userId = getUserId(state);

  return task.assigned_user_object_id === userId;
};

export const getPartnerDropdownState = (state: RootState): boolean => {
  const checklist = getChecklistUIState(state);

  return checklist.invitePartnerViewActive;
};

export const getChecklistTaskCompletedAtByTaskId = (
  state: RootState,
  taskId: string
): Date | undefined => {
  const task = getChecklistTaskById(state, taskId);

  return task.completed_at;
};

export const getTaskAddedByCopy = (
  state: RootState,
  taskId: string,
  userId: string
): string | null => {
  if (!userId) return null;

  const ownerId = getOwnerAccountUserId(state);
  const partnerId = getPartnerUserId(state);
  const isCurrentUser = getUserId(state) === userId;
  const isPartner = userId === partnerId;
  const isUser = ownerId === userId;

  if (isCurrentUser) {
    return 'You';
  }
  if (isUser) {
    return getOwnerFirstName(state);
  }
  if (isPartner) {
    return getPartnerFirstName(state);
  }

  return null;
};



// WEBPACK FOOTER //
// ./src/selectors/checklist/checklistSelectors.ts